import type { PromptCategory } from '@/types';

// Matches {{UPPER_SNAKE_CASE}} tokens. Mixed-case or lowercase tokens are ignored on purpose.
export const PLACEHOLDER_REGEX = /\{\{[A-Z][A-Z0-9_]*\}\}/g;

// Pro Kategorie: welche Placeholder werden vom zugehörigen Edge-Function-Flow tatsächlich ersetzt.
// Quelle: grep über supabase/functions/**/index.ts (Stand 2026-05-04).
// Wird ein Prompt mit unbekanntem Placeholder gespeichert, blockiert handleSave in
// AdminPromptConfigView.tsx den Save mit klarer Fehlermeldung.
export const PLACEHOLDERS_BY_CATEGORY: Record<PromptCategory, readonly string[]> = {
  // Leaf-Kategorien: Inhalt ist selbst der Wert für einen anderen Placeholder, daher keine eigenen.
  style: [],
  aspect_ratio: [],
  system: [],
  constraint: [],

  // generate-stage / tryout-generate
  room_empty: ['{{ASPECT_RATIO_INSTRUCTION}}', '{{ROOM_LABEL}}'],
  room_staged: ['{{ASPECT_RATIO_INSTRUCTION}}', '{{STYLE_INSTRUCTION}}', '{{ROOM_LABEL}}', '{{ROOM_TYPE}}', '{{STYLE}}', '{{PLACEMENT_LIST}}'],
  verification: ['{{ASPECT_RATIO_INSTRUCTION}}', '{{FEEDBACK}}'],

  // Two-stage staging reprompt pipeline (classify-stage-intent) + the 4
  // scope×intent stage_refine_* final-edit templates (generate-stage-refinement).
  // {{USER_PROMPT}} — used by the 4 stage_refine_* templates (fillStageTemplate).
  // {{REGION}} — the local-edit location hint (drawn box), used by the two
  // stage_refine_local_* templates only.
  // {{ROOM_STATE}} — the versionType-derived preservation clause (empty vs
  // staged), used by the four stage_refine_{scope}_{intent} templates.
  // {{SCOPE}}/{{INTENT}}/{{TARGET}} — used only by stage_reprompt_rewrite
  // (staging-reprompt-rewrite.ts); the classifier template itself takes no
  // placeholders (the raw user prompt is appended after it, not substituted).
  stage_refinement: ['{{USER_PROMPT}}', '{{REGION}}', '{{ROOM_STATE}}', '{{SCOPE}}', '{{INTENT}}', '{{TARGET}}'],

  // Leerräum-Berater (empty_consult_system) — siehe fillEmptyTemplate() in
  // _shared/classifiers/empty-room-prompt.ts.
  // {{BASE_TEMPLATE}} — die aufgelöste statische Leerräum-Anweisung
  // {{ROOM_TYPE}}     — Raumtyp
  // {{KEEP_KITCHEN}}  — "true" | "false"
  empty_consult: ['{{BASE_TEMPLATE}}', '{{ROOM_TYPE}}', '{{KEEP_KITCHEN}}'],

  // generate-floorplan (2D) — siehe buildPrompt() in generate-floorplan/index.ts
  floorplan_2d: [
    '{{FLOORING_INSTRUCTION}}',
    '{{TEXT_HANDLING_INSTRUCTION}}',
    '{{ROOM_LABEL_INSTRUCTION}}',
    '{{FIXED_INSTALLATIONS_INSTRUCTION}}',
  ],

  // generate-floorplan (3D) — Template wird unverändert durchgereicht, keine Ersetzung.
  floorplan_3d: [],

  // generate-floorplan-refinement — siehe fillTemplate() in floorplan-refinement-prompts.ts
  floorplan_refinement: [
    '{{MASK_CLAUSE}}',
    '{{OPERATION_INSTRUCTION}}',
    '{{SOURCE_FLOORPLAN_HINT}}',
  ],

  // Leaf-Kategorien: Inhalt ist Substitutionswert für einen Placeholder anderswo,
  // daher selbst keine Placeholder darin.
  floorplan_flooring: [],
  floorplan_text_handling: [],
  floorplan_room_label: [],
  floorplan_refinement_clause: [],
};

export function extractPlaceholders(content: string): string[] {
  const matches = content.match(PLACEHOLDER_REGEX);
  return matches ? Array.from(new Set(matches)) : [];
}

export function findUnknownPlaceholders(
  content: string,
  category: PromptCategory,
): string[] {
  const allowed = new Set(PLACEHOLDERS_BY_CATEGORY[category]);
  return extractPlaceholders(content).filter((p) => !allowed.has(p));
}
